import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Actions = styled.div`
  ${actionContainerStyles};
  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }
  li + li {
    margin-top: 4px;
  }
  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;
    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
  @media (min-width: 768px) {
    flex: 0 0 260px;
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
  }

  tbody td {
    ${adminTableDataCell};
  }
`;

const LinkButton = styled.a`
  margin-right: 8px;
  color: white;
  background: ${({ colors }) => colors.buttonBackground};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const Form = styled.form`
  background: ${({ colors }) => colors.cardBackground};
  padding: 20px;
  border-radius: 5px;
`;

const Field = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.3em;
    color: ${({ colors }) => colors.labelText};
    font-size: 14px;
  }
  input[type="text"] {
    width: 100%;
    padding: 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 14px;
  }
`;

const SubmitRow = styled.div`
  margin-top: 1.5em;
  input[type="submit"] {
    padding: 8px 16px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

export default function OrganizationTypesContent({ colors }) {
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);

  // view: 'list' | 'add' | 'edit'
  const [view, setView] = useState("list");

  // for edit/add
  const [selected, setSelected] = useState(null);
  const [originalType, setOriginalType] = useState("");
  const [originalDesc, setOriginalDesc] = useState("");
  const [typeInput, setTypeInput] = useState("");
  const [descInput, setDescInput] = useState("");

  // confirm/delete modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteType, setPendingDeleteType] = useState(null);

  // result modal
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");

  // load organization types
  const loadTypes = async () => {
    setLoading(true);
    try {
      const resp = await API.getOrganizationType({});
      setTypes(resp.result || []);
    } catch (err) {
      console.error("Failed to load organization types:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  // open delete confirmation
  const onDeleteClick = (type) => {
    setPendingDeleteType(type);
    setConfirmOpen(true);
  };

  // actually perform the delete
  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);
    try {
      const resp = await API.deleteOrganizationType({ type: pendingDeleteType });
      setModalTitle(resp?.status === "Failed" ? "Error" : "Deleted");
      setModalMessage(resp?.message || `Type "${pendingDeleteType}" has been deleted.`);
      setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
      await loadTypes();
    } catch (err) {
      console.error("Delete failed:", err);
      setModalTitle("Error");
      setModalMessage("Failed to delete that type.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
      setPendingDeleteType(null);
    }
  };

  // open edit form
  const openEdit = (ot) => {
    setSelected(ot);
    setOriginalType(ot.org_type);
    setOriginalDesc(ot.org_type_desc);
    setTypeInput(ot.org_type);
    setDescInput(ot.org_type_desc);
    setView("edit");
  };

  // submit update
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const resp = await API.updateOrganizationType({
        type: typeInput,
        description: descInput,
        old_type: originalType,
        old_desc: originalDesc,
      });
      setModalTitle(resp?.status === "Failed" ? "Error" : "Success");
      setModalMessage(resp?.message || "Organization type updated.");
      setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
      await loadTypes();
      setView("list");
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Update failed.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  // submit add
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const resp = await API.addOrganizationType({
        type: typeInput,
        description: descInput,
      });
      setModalTitle(resp?.status === "Failed" ? "Error" : "Success");
      setModalMessage(resp?.message || "Organization type added.");
      setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
      await loadTypes();
      setView("list");
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Add failed.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  if (loading) return <p>Loading…</p>;

  return (
    <Container>
      {/* Delete confirmation */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm delete"
        message={`Are you sure you want to delete "${pendingDeleteType}"?`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Result modal */}
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      {/* Actions pane */}
      <ResponsiveActions
        colors={colors}
        renderActions={() => (
          <Actions colors={colors}>
            <h3>Actions</h3>
            <ul>
              {view === "list" && (
                <li>
                  <a
                    onClick={() => {
                      setView("add");
                      setTypeInput("");
                      setDescInput("");
                    }}
                  >
                    Add
                  </a>
                </li>
              )}
              {(view === "add" || view === "edit") && (
                <li>
                  <a onClick={() => setView("list")}>Cancel</a>
                </li>
              )}
            </ul>
          </Actions>
        )}
      />

      <View>
        {view === "list" && (
          <Table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.org_type}>
                  <td>{t.org_type}</td>
                  <td>{t.org_type_desc}</td>
                  <td>
                    <LinkButton
                      colors={colors}
                      onClick={() => openEdit(t)}
                    >
                      Edit
                    </LinkButton>
                    <LinkButton
                      colors={colors}
                      onClick={() => onDeleteClick(t.org_type)}
                    >
                      Delete
                    </LinkButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {view === "edit" && (
          <>
            <h1 style={{ color: colors.labelText }}>
              Edit Organization Type
            </h1>
            <Form colors={colors} onSubmit={handleUpdate}>
              <Field colors={colors}>
                <label>Type (lowercased)</label>
                <input
                  type="text"
                  value={typeInput}
                  onChange={(e) =>
                    setTypeInput(e.target.value.toLowerCase())
                  }
                />
              </Field>
              <Field colors={colors}>
                <label>Description</label>
                <input
                  type="text"
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                />
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit" />
              </SubmitRow>
            </Form>
          </>
        )}

        {view === "add" && (
          <>
            <h1 style={{ color: colors.labelText }}>
              Add An Organization Type
            </h1>
            <Form colors={colors} onSubmit={handleAdd}>
              <Field colors={colors}>
                <label>Type (lowercased)</label>
                <input
                  type="text"
                  value={typeInput}
                  onChange={(e) =>
                    setTypeInput(e.target.value.toLowerCase())
                  }
                />
              </Field>
              <Field colors={colors}>
                <label>Description</label>
                <input
                  type="text"
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                />
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit" />
              </SubmitRow>
            </Form>
          </>
        )}
      </View>

      <div style={{ clear: "both" }} />
    </Container>
  );
}
